"use client";

import { useEffect, useRef } from "react";
import { preload } from "react-dom";
import gsap from "gsap";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { ArrowScribble, UnderlinedWord } from "@/components/brand/Scribble";
import { BookCall } from "@/components/booking/BookCall";
import { LayeredText } from "@/components/ui/layered-text";
import { ScrambledText } from "@/components/ui/scrambled-text";
import { buttonVariants } from "@/components/ui/button";
import { afterSplash, splashPlayed } from "@/lib/splash";
import { cn } from "@/lib/utils";

// What the studio makes, and how: the deliverables above the line, the promises below it.
// The stack centres itself on the emphasised steps, so phrases can be added anywhere in
// this list and the line stays on the centre.
const LADDER = [
  { text: "LANDING PAGES" },
  { text: "WEB APPS" },
  { text: "BRAND FILMS" },
  { text: "SOCIAL KITS" },
  { text: "AI AGENTS" },
  { text: "DASHBOARDS" },
  { text: "DEAL ROOMS" },
  { text: "PRODUCT REELS" },
  { text: "MOBILE APPS" },
  { text: "CUSTOM CRMS" },
  { text: "ERPS" },
  { text: "GOOD TASTE,", emphasis: true },
  { text: "SHIPPED FAST.", emphasis: true },
  { text: "ONE PACKAGE" },
  { text: "WEEKLY DROPS" },
  { text: "FIXED DATES" },
  { text: "NO SURPRISES" },
  { text: "REAL DEVICES" },
  { text: "CHECKED TWICE" },
  { text: "BUILT TO LAST" },
  { text: "ON BRAND" },
  { text: "ON TIME" },
  { text: "STILL FAST" },
  { text: "A YEAR LATER" },
];

/**
 * Home screen. The logo is static and the copy and chrome fade up on mount.
 *
 * From lg up the hero splits in two: logo on the left with the paragraph and
 * CTAs directly under it, and the layered staircase of phrases on the right.
 * Below lg the staircase is dropped and the h1 carries the line on its own.
 */
export const Hero = () => {
  // Ship the mask SVGs as early as possible: the logo is the LCP element.
  preload("/brand/wordmark.svg", { as: "image", crossOrigin: "anonymous" });
  preload("/brand/studio.svg", { as: "image", crossOrigin: "anonymous" });

  const section = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();

  // Scroll-linked: the copy lifts and fades as the hero scrolls away. The logo stays put.
  const { scrollYProgress } = useScroll({ target: section, offset: ["start start", "end start"] });
  const copyY = useTransform(scrollYProgress, [0, 1], ["0vh", "-10vh"]);
  const fade = useTransform(scrollYProgress, [0, 0.55], [1, 0]);

  // Entrance: copy and chrome rise as the page mounts.
  useEffect(() => {
    const root = section.current;
    if (!root) return;

    const cleanups: (() => void)[] = [];
    const ctx = gsap.context(() => {
      const copy = gsap.utils.toArray<HTMLElement>("[data-copy]");
      const chrome = gsap.utils.toArray<HTMLElement>("[data-chrome]");

      const wordmarkLayer = root.querySelector<HTMLElement>("[data-logo-wordmark]");
      const scriptLayer = root.querySelector<HTMLElement>("[data-logo-studio]");
      const inkLayers = [wordmarkLayer, scriptLayer].filter(Boolean) as HTMLElement[];

      if (reduced) {
        gsap.set([...copy, ...chrome], { opacity: 1, y: 0 });
        // The logo is the LCP element: with motion off it must still end fully
        // painted, so clear the clips the reveal would otherwise animate.
        gsap.set(inkLayers, { clipPath: "none", opacity: 1 });
        return;
      }

      // Behind the first-load splash the logo has already been written, and globals.css
      // shows it in full under the sheet. So the logo steps are dropped, and the copy waits
      // for the sheet to start lifting instead of rising unseen behind it.
      const splash = splashPlayed();
      const tl = gsap.timeline({ delay: splash ? 0 : 0.15, paused: splash });

      // 1. The wordmark rises into view behind a hard edge, letter block by
      //    letter block. `inset()` from the bottom means the clip travels up the
      //    letterforms rather than fading them, which reads as ink being laid
      //    down instead of a dissolve.
      if (wordmarkLayer && !splash) {
        tl.fromTo(
          wordmarkLayer,
          { clipPath: "inset(0 0 100% 0)" },
          { clipPath: "inset(0 0 0% 0)", duration: 1.05, ease: "power3.inOut" },
          0,
        );
      }

      // 2. The script is brushed on left to right, chasing the wordmark. The
      //    artwork is filled outlines rather than centreline strokes, so a
      //    dash-offset "draw" would trace the edge of each brush mark instead of
      //    running along it. A directional wipe follows the writing hand, which
      //    is the effect that reads as handwriting.
      if (scriptLayer && !splash) {
        tl.fromTo(
          scriptLayer,
          { clipPath: "inset(0 100% 0 0)" },
          { clipPath: "inset(0 0% 0 0)", duration: 1.15, ease: "power2.out" },
          0.55,
        );
      }

      tl.fromTo(
        copy,
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.9, ease: "power3.out", stagger: 0.09 },
        splash ? 0.15 : 0.95,
      ).fromTo(
        chrome,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.1 },
        "<0.25",
      );
      if (splash) cleanups.push(afterSplash(() => tl.play()));
    }, root);

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      ctx.revert();
    };
  }, [reduced]);

  return (
    <section
      id="hero"
      ref={section}
      aria-label="Intro"
      className="hero relative isolate h-[100svh] min-h-[640px] w-full overflow-hidden bg-white text-brand-ink pt-14 sm:pt-[68px]"
    >
      {/* ── Logo (static, left column on desktop) ────────────────────────────── */}
      <div className="hero-logo-slot">
        <Logo className="w-full" animatable />
      </div>

      {/* ── Word ladder (right column, desktop only) ─────────────────────────── */}
      {/* Above the copy, but click-through: the rows are wider than their column
          and sprawl over the buttons, so without this they would swallow the
          clicks. The stack is decorative and aria-hidden, so nothing is lost. */}
      <div className="hero-ladder-slot pointer-events-none z-30">
        <div className="hero-ladder-anchor">
          <motion.div style={{ y: copyY, opacity: fade }}>
            {/* No srText: the h1 above already reads the same line, and the
                surrounding phrases are texture rather than content. */}
            <LayeredText
              lines={LADDER}
              startDelay={0.3}
              fontSize="70px"
              lineHeight={63}
              stepX={33}
              mirror
              className="text-brand-ink"
            />
          </motion.div>
        </div>
      </div>

      {/* ── Copy ─────────────────────────────────────────────────────────────── */}
      <div className="hero-copy-slot z-10">
        <motion.div style={{ y: copyY, opacity: fade }} className="flex flex-col items-center lg:items-start">
          {/* On desktop the word ladder is the visual headline, so the h1 drops
              to screen readers only; below lg it stays the on-screen hook. */}
          <h1
            data-copy
            className="max-w-2xl text-balance text-2xl font-bold leading-[1.1] text-brand-ink sm:text-3xl md:text-[2.6rem] lg:sr-only"
            style={{ opacity: 0 }}
          >
            <span className="sr-only">JRAT&apos;S Studio, websites, apps and content from Ahmedabad: </span>
            Good taste, <UnderlinedWord color="var(--brand-accent)" delay={1.5}>shipped fast.</UnderlinedWord>
          </h1>
          {/* The two words the whole offer turns on are picked out in pine, and the line
              scrambles under the pointer. SplitText keeps the accent spans, so those
              characters stay green while they churn. */}
          <ScrambledText
            data-copy
            radius={110}
            duration={1}
            speed={0.45}
            className="mt-4 max-w-xl text-balance text-sm leading-relaxed text-brand-ink/60 sm:text-base"
            style={{ opacity: 0 }}
          >
            Hand us the <span className="font-semibold text-brand-accent">tech</span> and the{" "}
            <span className="font-semibold text-brand-accent">content</span>. We build the site, the
            app or the system behind your business, run the feed in front of it, and ship all of it
            with taste, fast.
          </ScrambledText>
          <div
            data-copy
            className="relative mt-7 flex flex-wrap items-center justify-center gap-3 lg:justify-start"
            style={{ opacity: 0 }}
          >
            {/* Scrawled arrow leaning into the booking button. Hidden on the narrowest
                screens, where it would run into the edge of the copy column. On lg the
                copy sits flush left, so it pulls in to stay inside the page gutter. */}
            <ArrowScribble
              delay={2.2}
              color="var(--brand-accent)"
              className="pointer-events-none absolute -left-16 -top-7 hidden h-11 w-12 -rotate-[18deg] sm:block lg:-left-12"
            />
            <BookCall className={cn(buttonVariants())}>
              <span>Let&apos;s talk about your project</span>
              <ArrowUpRight className="size-4" />
            </BookCall>
            <a href="#work" className={cn(buttonVariants({ variant: "outline" }))}>
              See our work
            </a>
          </div>
        </motion.div>
      </div>

      {/* The scroll cue that used to sit here is gone. Someone looking at the hero has not
          scrolled yet and knows how; labelling the bottom of the viewport is decoration. */}
    </section>
  );
};
